import gql from 'graphql-tag';
import client from './apollo';
import {
  // firebaseUpdateSlide,
  removeSlides,
  firebaseUpdatePresentationMetaData,
  savePresentationUpdates,
} from '../../editor/actions/firebaseObjectActions';

export const createPresentationMutation = (
  name,
  nameDraft,
  width,
  height,
  tags,
  team,
  channel,
  themeId,
  googleDriveId,
  lastUpdatedBy,
) =>
  client.mutate({
    mutation: gql`
      mutation createPresentation(
        $name: String
        $nameDraft: String
        $width: Int!
        $height: Int!
        $tags: [String]
        $team: UUID
        $channel: UUID
        $themeId: UUID
        $googleDriveId: String
        $lastUpdatedBy: UUID
      ) {
        createPresentation(
          input: {
            name: $name
            nameDraft: $nameDraft
            width: $width
            height: $height
            tags: $tags
            team: $team
            channel: $channel
            themeId: $themeId
            googleDriveId: $googleDriveId
            lastUpdatedBy: $lastUpdatedBy
          }
        ) {
          success
          presentation {
            id
            hash
          }
          validationErrors {
            field
            code
            message
            extra
          }
        }
      }
    `,
    variables: {
      name,
      nameDraft,
      width,
      height,
      tags,
      team,
      channel,
      themeId,
      googleDriveId,
      lastUpdatedBy,
    },
  });

export const savePresentationMutation = (
  id,
  version,
  add,
  update,
  remove,
  name = null,
  description = null,
  slug = null,
  password = null,
  order = null,
  thumbnail = null,
) =>
  client.mutate({
    mutation: gql`
      mutation savePresentation(
        $id: UUID!
        $version: String!
        $add: [SlideInput]
        $update: [SlideInput]
        $remove: [UUID]
        $name: String
        $description: String
        $slug: String
        $password: String
        $order: [UUID]
        $thumbnail: String!
      ) {
        savePresentation(
          input: {
            id: $id
            version: $version
            add: $add
            update: $update
            delete: $remove
            name: $name
            description: $description
            slug: $slug
            password: $password
            order: $order
            thumbnail: $thumbnail
          }
        ) {
          success
          presentation {
            id
            version
          }
          added {
            id
            version
          }
          updated {
            id
            version
          }
          deleted
          validationErrors {
            field
            code
            message
            extra
          }
        }
      }
    `,
    variables: {
      id,
      version,
      add,
      update,
      remove,
      name,
      description,
      slug,
      password,
      order,
      thumbnail,
    },
  });

export const savePresentationToFirebase = async ({
  id,
  version,
  publishedVersion,
  add,
  update,
  remove,
  name = null,
  description = null,
  slug = null,
  password = null,
  scrollableViewer,
  hideControls,
  order = null,
  authorId = null,
  content,
  silentUpdate,
}) => {
  var updates = {
    ...content,
    ...firebaseUpdatePresentationMetaData(id, {
      version,

      name,
      description,
      slug: null,
      password: null,
      scrollable_viewer: scrollableViewer,
      hide_controls: hideControls,
      order,
      ...(!silentUpdate && { updated_at: new Date().getTime() }),
    }),
  };
  var removes = removeSlides(id, remove);
  await savePresentationUpdates({ ...removes, ...updates });

  return {
    data: {
      savePresentation: {
        success: true,
        presentation: {
          id,
          version,
        },
        added: add.map(slide => ({ id: slide.id })),
        updated: update.map(slide => ({ id: slide.id })),
        deleted: remove,
        validationErrors: null,
      },
    },
  };
};

export const publishPresentationMutation = (id, thumbnail) =>
  client.mutate({
    mutation: gql`
      mutation publishPresentation($id: UUID!, $thumbnail: String) {
        publishPresentation(
          input: { presentationId: $id, thumbnail: $thumbnail }
        ) {
          success
          validationErrors {
            field
            code
            message
            extra
          }
        }
      }
    `,
    variables: {
      id,
      thumbnail,
    },
  });

export const migratePresentationMutation = id =>
  client.mutate({
    mutation: gql`
      mutation migratePresentation($id: UUID!) {
        migratePresentation(input: { presentationId: $id }) {
          success
          validationErrors {
            field
            code
            message
            extra
          }
        }
      }
    `,
    variables: {
      id,
    },
  });

export const addNotesMutation = (id, notes) =>
  savePresentationUpdates(
    firebaseUpdatePresentationMetaData(window.presentationId, {
      [`slides/${id}/notes`]: notes,
    }),
  ).then(() => ({
    data: {
      updateSlide: {
        success: true,
      },
    },
  }));

export const prepareUploadMutation = gql`
  mutation prepareUpload($mimeType: String!, $size: Float!, $teamId: UUID) {
    prepareUpload(
      input: { mimeType: $mimeType, size: $size, teamId: $teamId }
    ) {
      success
      upload {
        uploadUrl
        uploadService
        uploadParams
        fileUrl
        fileFormat
      }
      validationErrors {
        field
        code
        message
        extra
      }
    }
  }
`;

export const createFileMutation = gql`
  mutation createFile(
    $mimeType: String!
    $remoteUrl: String!
    $service: String!
    $size: Float!
    $teamId: UUID
  ) {
    createFile(
      input: {
        mimeType: $mimeType
        remoteUrl: $remoteUrl
        service: $service
        size: $size
        teamId: $teamId
      }
    ) {
      success
      file {
        id
        remoteUrl
        mimeType
      }
    }
  }
`;

export const addMediaToSlideMutation = gql`
  mutation addMediaToSlide($slide: UUID!, $file: UUID!) {
    addMediaToSlide(input: { slide: $slide, file: $file }) {
      success
      media {
        id
        remoteUrl
        mimeType
      }
      validationErrors {
        field
        code
        message
      }
    }
  }
`;

export const removeMediaFromSlideMutation = gql`
  mutation removeMediaFromSlide($slide: UUID!) {
    removeMediaFromSlide(input: { slide: $slide }) {
      success
      validationErrors {
        field
        code
        message
      }
    }
  }
`;

export const addFeaturedFontMutation = gql`
  mutation addFeaturedFont($id: UUID!, $teamId: UUID) {
    addFeaturedFont(input: { id: $id, teamId: $teamId }) {
      success
    }
  }
`;

export const updateWalkthroughProgressMutation = gql`
  mutation updateWalkthroughProgress(
    $app: String!
    $code: String!
    $status: String!
    $step: Int!
    $total: Int!
  ) {
    updateWalkthroughProgress(
      input: {
        app: $app
        code: $code
        status: $status
        step: $step
        total: $total
      }
    ) {
      success
    }
  }
`;

export const updateGoogleDriveIdMutation = (id, value) =>
  new Promise((resolve, reject) => {
    client
      .mutate({
        mutation: gql`
          mutation updatePresentation($id: UUID!, $googleDriveId: String) {
            updatePresentation(
              input: { id: $id, googleDriveId: $googleDriveId }
            ) {
              success
              validationErrors {
                field
                code
                message
                extra
              }
            }
          }
        `,
        variables: {
          id,
          googleDriveId: value,
        },
      })
      .then(result => {
        savePresentationUpdates(
          firebaseUpdatePresentationMetaData(id, {
            google_drive_id: value,
          }),
        ).then(() => {
          resolve(result);
        });
      });
  });

export const updateFirebaseMigratedMutation = gql`
  mutation updatePresentation($id: UUID!, $firebaseMigrated: Boolean) {
    updatePresentation(
      input: { id: $id, firebaseMigrated: $firebaseMigrated }
    ) {
      success
      presentation {
        id
        firebaseMigrated
      }
      validationErrors {
        field
        code
        message
        extra
      }
    }
  }
`;
